import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import type { OutboxHydrationState } from '@Domain/Ports/Outbox/OutboxPort';
import resolveInterviewDetail, {
	type InterviewDetailsSource,
} from '@UI/Pages/Beneficiaries/Hooks/useRetrieveInterviewDetail/resolveInterviewDetail';
import useBeneficiaryInterviewsList from '@UI/Pages/Beneficiaries/Hooks/useBeneficiaryInterviewsList';
import useOutboxItems from '@UI/Providers/PortsProvider/Hooks/OutboxRunner/useOutboxItems';
import useOutboxPort from '@UI/Providers/PortsProvider/Hooks/OutboxRunner/useOutboxPort';
import * as React from 'react';
import type InternalInterviewDetailsView from '@UI/Pages/Beneficiaries/Hooks/useRetrieveInterviewDetail/InternalInterviewDetailsView';

export type InterviewDetailRetrieveResult =
	| { readonly type: 'loading' }
	| { readonly type: 'error'; readonly error: Error; readonly refetch: () => void }
	| { readonly type: 'not-found' }
	| {
			readonly type: 'resolved';
			readonly interviewDetails: InternalInterviewDetailsView;
			readonly refetch: () => void;
			/** The server list's own failure, SIGNALED rather than acted on: this hook no longer
			 * decides whether a resolved subject or a server failure wins, each caller does. */
			readonly error: Error | null;
	  };

/**
 * Resolves the subject of the detail screen from `useBeneficiaryInterviewsList`'s three additive
 * sources, plus the transfer queue read directly here — the same snapshot `index.tsx`
 * already reads, since the port itself is the single source of truth.
 *
 * Branch order: a resolved `detail` wins first, and carries whatever server `error` exists alongside
 * it — a purely local interview renders even while the server list has never answered, or has failed
 * outright. Precedence between "show the subject" and "show the failure" is NOT decided here anymore:
 * it is a display policy, and each screen that calls this hook knows which policy it needs (a
 * consultation screen refuses a half-rendered subject; a processing screen, entered from the very
 * start of an upload, has every reason to have nothing else yet). Only once no subject can be resolved
 * at all does `error` decide between the `'error'` and `'loading'` branches; `'not-found'` is
 * pronounced only once both sources have loaded (`isServerDataLoaded` and `isLocalStoreHydrated` are
 * each a hard signal, `useStoreQuery`'s own `data !== null` and `LocalInterviewPort.isHydrated()`),
 * never after a delay or a timer.
 *
 * `outboxHydration` is read straight from `OutboxPort.hydrationState()` through
 * `useSyncExternalStore` and exposed as an additive field alongside the existing `type` union: it
 * never changes which of the four branches above is picked, since the queue only ever decorates an
 * already-resolved subject (see `resolveInterviewDetail`), never resolves one by itself. Rendering
 * the three hydration states — including the `'failed'` retry affordance — is a display policy each
 * calling screen owns for itself.
 */
function useRetrieveInterviewDetails(
	subject: SubjectAddress,
	target: InterviewDetailsSource,
): InterviewDetailRetrieveResult & { readonly outboxHydration: OutboxHydrationState } {
	const {
		serverInterviews,
		localInterviews,
		participantLabels,
		error,
		refetch,
		isServerDataLoaded,
		isLocalStoreHydrated,
	} = useBeneficiaryInterviewsList(subject);

	const outboxItems = useOutboxItems();

	const outboxPort = useOutboxPort();
	const subscribeToOutboxHydration = React.useCallback(
		(onStoreChange: () => void): (() => void) => outboxPort.subscribe(() => onStoreChange()),
		[outboxPort],
	);
	const getOutboxHydrationSnapshot = React.useCallback(
		(): OutboxHydrationState => outboxPort.hydrationState(),
		[outboxPort],
	);
	const outboxHydration = React.useSyncExternalStore(
		subscribeToOutboxHydration,
		getOutboxHydrationSnapshot,
	);

	// `target` is reconstructed by the caller on every render; the memo depends on the primitive
	// identifiers rather than on `target` itself, so a caller that never changes which interview it
	// names never recomputes the resolution on every render.
	const serverInterviewId = target.type === 'server' ? target.interviewId : null;
	const localInterviewId = target.type === 'local' ? target.localInterviewId : null;

	const interviewDetails = React.useMemo<InternalInterviewDetailsView | null>(() => {
		if (serverInterviewId !== null) {
			return resolveInterviewDetail(
				{ type: 'server', interviewId: serverInterviewId },
				serverInterviews,
				localInterviews,
				outboxItems,
				participantLabels,
			);
		}

		if (localInterviewId !== null) {
			return resolveInterviewDetail(
				{ type: 'local', localInterviewId },
				serverInterviews,
				localInterviews,
				outboxItems,
				participantLabels,
			);
		}

		return null;
	}, [
		serverInterviewId,
		localInterviewId,
		serverInterviews,
		localInterviews,
		outboxItems,
		participantLabels,
	]);

	return React.useMemo(() => {
		if (interviewDetails !== null) {
			return { type: 'resolved', interviewDetails, refetch, error, outboxHydration };
		}

		if (error !== null) {
			return { type: 'error', error, refetch, outboxHydration };
		}

		if (!isServerDataLoaded || !isLocalStoreHydrated) {
			return { type: 'loading', outboxHydration };
		}

		return { type: 'not-found', outboxHydration };
	}, [interviewDetails, error, isServerDataLoaded, isLocalStoreHydrated, refetch, outboxHydration]);
}

export default useRetrieveInterviewDetails;
